import React, { useEffect, useState } from 'react'
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useLanguage } from '../../services/language/LanguageContext';
import { IsEmptyGuid } from '../../helper/Formatters';
import { Modal, Form, Row, Col, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

const extractCoordsFromMapsUrl = (url) => {
    const patterns = [
        /@(-?\d+\.\d+),(-?\d+\.\d+)/,
        /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
        /q=(-?\d+\.\d+),(-?\d+\.\d+)/,
        /ll=(-?\d+\.\d+),(-?\d+\.\d+)/,
    ];
    for (const p of patterns) {
        const m = url.match(p);
        if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
    }
    return null;
};

const AddressesBox = ({ euid, userId, defaultName = '', defaultPhone = '' }) => {
    const api = useApiServices();
    const { t } = useLanguage();
    const [addresses, setAddresses] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [mapsLink, setMapsLink] = useState('');
    const [mapsStatus, setMapsStatus] = useState(null); // 'ok' | 'fail' | null
    const [addressData, setAddressData] = useState({
        name: '', addressLine1: '', addressLine2: '',
        latitude: 0, longitude: 0, postalCode: '', country: '',
        userId: userId ?? null, isDefault: true, isDeleted: false,
    });
    const [contact, setContact] = useState({ name: '', phone: '' });

    const fetchAddresses = async () => {
        const { isSuccess, result } = await api.GetAddressAsync(euid);
        if (isSuccess && result) setAddresses(result);
    };

    const handleToggleDefault = async (address) => {
        const { isSuccess } = await api.UpdateAddressAsync(euid, { ...address, isDefault: !address.isDefault });
        if (isSuccess) toast.success(t('save'));
        else toast.error('บางอย่างผิดพลาด');
        fetchAddresses();
    };

    const handleOpenModal = (address = null) => {
        if (address) {
            setEditingAddress(address);
            setAddressData(address);
            setContact(address.contact || { name: '', phone: '' });
        } else {
            setEditingAddress(null);
            setAddressData({
                name: '', addressLine1: '', addressLine2: '',
                latitude: 0, longitude: 0, postalCode: '', country: '',
                userId: userId ?? null, isDefault: false, isDeleted: false,
            });
            setContact({ name: defaultName ?? '', phone: defaultPhone ?? '' });
        }
        setErrors({});
        setMapsLink('');
        setMapsStatus(null);
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setAddressData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleChangeContact = (e) => {
        const { name, value } = e.target;
        setContact(prev => ({ ...prev, [name]: value }));
    };

    const handleMapsLinkChange = (e) => {
        const url = e.target.value;
        setMapsLink(url);
        if (!url.trim()) { setMapsStatus(null); return; }
        const coords = extractCoordsFromMapsUrl(url);
        if (coords) {
            setAddressData(prev => ({ ...prev, latitude: coords.lat, longitude: coords.lng }));
            setMapsStatus('ok');
        } else {
            setMapsStatus('fail');
        }
    };

    const handleSave = async () => {
        const newErrors = {};
        if (!contact.name.trim()) newErrors.name = t('recipient_name') + ' จำเป็น';
        if (!contact.phone.trim()) newErrors.phone = t('recipient_phone') + ' จำเป็น';
        if (!addressData.addressLine1.trim()) newErrors.addressLine1 = t('address_line1') + ' จำเป็น';
        if (!addressData.postalCode.trim()) newErrors.postalCode = t('postal_code') + ' จำเป็น';
        if (!addressData.latitude || addressData.latitude === 0) newErrors.latitude = t('latitude') + ' จำเป็น';
        if (!addressData.longitude || addressData.longitude === 0) newErrors.longitude = t('longitude') + ' จำเป็น';
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        setSaving(true);
        const payload = {
            ...addressData,
            name: contact.name,
            contact: { name: contact.name, phone: contact.phone },
        };
        const { isSuccess } = editingAddress
            ? await api.UpdateAddressAsync(euid, payload)
            : await api.AddAddressAsync(euid, payload);
        setSaving(false);

        if (isSuccess) toast.success(t('save'));
        else toast.error('บางอย่างผิดพลาด');
        handleCloseModal();
        fetchAddresses();
    };

    useEffect(() => {
        if (euid && !IsEmptyGuid(euid)) {
            fetchAddresses();
            setContact({ name: defaultName, phone: defaultPhone });
        }
    }, [euid]);

    const hasCoords = addressData.latitude !== 0 || addressData.longitude !== 0;

    return (
        <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('address_list')}
                    </span>
                    {addresses.length > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '1px 7px' }}>
                            {addresses.length}
                        </span>
                    )}
                </div>
                {!IsEmptyGuid(euid) && (
                    <button onClick={() => handleOpenModal()} style={addBtnStyle}>
                        <i className="fa fa-plus" style={{ fontSize: 10 }} />{t('add_address')}
                    </button>
                )}
            </div>

            {/* Address List */}
            {addresses.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13, background: '#f9fafb', borderRadius: 10 }}>
                    <i className="fas fa-map-marker-alt" style={{ fontSize: 20, marginBottom: 6, display: 'block', opacity: 0.4 }} />
                    {t('no_address')}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto', paddingRight: 2 }}>
                    {addresses.map((address) => (
                        <div key={address.id} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 12,
                            padding: '12px 14px', borderRadius: 10,
                            background: address.isDefault ? '#fff' : '#f9fafb',
                            border: `1px solid ${address.isDefault ? '#e0e7ff' : '#e5e7eb'}`,
                            transition: 'all 0.15s',
                        }}>
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                background: address.isDefault ? '#e0e7ff' : '#f3f4f6',
                                border: `1.5px solid ${address.isDefault ? '#c7d2fe' : '#e5e7eb'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 15, color: address.isDefault ? '#4f46e5' : '#9ca3af',
                            }}>
                                <i className="fas fa-map-marker-alt" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937', marginBottom: 2 }}>
                                    {address.contact?.name || address.name || '-'}
                                </div>
                                {address.contact?.phone && (
                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>
                                        <i className="fa fa-phone" style={{ marginRight: 5, fontSize: 10, color: '#9ca3af' }} />
                                        {address.contact.phone}
                                    </div>
                                )}
                                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                                    {address.addressLine1}
                                    {address.addressLine2 && <>, {address.addressLine2}</>}
                                    {(address.postalCode || address.country) && (
                                        <span style={{ color: '#9ca3af' }}>
                                            {address.postalCode && ` ${address.postalCode}`}
                                            {address.country && `, ${address.country}`}
                                        </span>
                                    )}
                                </div>
                                {(address.latitude !== 0 || address.longitude !== 0) && (
                                    <a
                                        href={`https://www.google.com/maps?q=${address.latitude},${address.longitude}`}
                                        target="_blank" rel="noopener noreferrer"
                                        style={{ fontSize: 11, color: '#6366f1', marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
                                    >
                                        <i className="fas fa-map-pin" style={{ fontSize: 9 }} />
                                        {address.latitude}, {address.longitude}
                                    </a>
                                )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                                <span style={{
                                    fontSize: 10, fontWeight: 600, borderRadius: 10, padding: '2px 7px',
                                    background: address.isDefault ? '#dcfce7' : '#f3f4f6',
                                    color: address.isDefault ? '#16a34a' : '#9ca3af',
                                }}>
                                    {address.isDefault ? t('default_badge') : '—'}
                                </span>
                                <button onClick={() => handleOpenModal(address)} style={textBtnStyle}>
                                    {t('edit')}
                                </button>
                                <button onClick={() => handleToggleDefault(address)} style={{ ...textBtnStyle, color: address.isDefault ? '#f87171' : '#22c55e' }}>
                                    {address.isDefault ? t('unset_default') : t('set_as_default')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '18px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <i className="fas fa-map-marker-alt" style={{ fontSize: 15, color: '#fff' }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                                {editingAddress ? t('edit_address') : t('add_address')}
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{t('delivery_address_info')}</div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px 24px' }}>

                    {/* Recipient */}
                    <div style={sectionLabelStyle}>{t('recipient_info')}</div>
                    <Row className="mb-3">
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label style={labelStyle}>{t('recipient_name')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                <Form.Control name="name" value={contact.name} onChange={handleChangeContact}
                                    style={{ ...inputStyle, borderColor: errors.name ? '#ef4444' : undefined }}
                                    placeholder="ชื่อ-นามสกุล" />
                                {errors.name && <div style={errStyle}>{errors.name}</div>}
                            </Form.Group>
                        </Col>
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label style={labelStyle}>{t('recipient_phone')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                <Form.Control name="phone" value={contact.phone} onChange={handleChangeContact}
                                    style={{ ...inputStyle, borderColor: errors.phone ? '#ef4444' : undefined }}
                                    placeholder="0xx-xxx-xxxx" />
                                {errors.phone && <div style={errStyle}>{errors.phone}</div>}
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Address */}
                    <div style={sectionLabelStyle}>{t('address_info')}</div>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>{t('address_line1')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                        <Form.Control name="addressLine1" value={addressData.addressLine1} onChange={handleChange}
                            style={{ ...inputStyle, borderColor: errors.addressLine1 ? '#ef4444' : undefined }}
                            placeholder="บ้านเลขที่ ถนน ซอย" />
                        {errors.addressLine1 && <div style={errStyle}>{errors.addressLine1}</div>}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>{t('address_line2')}</Form.Label>
                        <Form.Control name="addressLine2" value={addressData.addressLine2} onChange={handleChange}
                            style={inputStyle} placeholder="แขวง/ตำบล เขต/อำเภอ จังหวัด (ถ้ามี)" />
                    </Form.Group>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={labelStyle}>{t('postal_code')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                <Form.Control name="postalCode" value={addressData.postalCode} onChange={handleChange}
                                    style={{ ...inputStyle, fontFamily: 'monospace', borderColor: errors.postalCode ? '#ef4444' : undefined }}
                                    placeholder="10xxx" />
                                {errors.postalCode && <div style={errStyle}>{errors.postalCode}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={labelStyle}>{t('country_label')}</Form.Label>
                                <Form.Control name="country" value={addressData.country} onChange={handleChange}
                                    style={inputStyle} placeholder="Thailand" />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* GPS */}
                    <div style={sectionLabelStyle}>{t('gps_coordinates')}</div>

                    {/* Maps paste */}
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>
                            <i className="fa fa-map-o" style={{ marginRight: 6, color: '#4f46e5' }} />
                            {t('google_maps_link')}
                        </Form.Label>
                        <div style={{ position: 'relative' }}>
                            <Form.Control
                                value={mapsLink} onChange={handleMapsLinkChange}
                                placeholder={t('google_maps_placeholder')}
                                style={{ ...inputStyle, paddingRight: mapsStatus ? 36 : undefined }}
                            />
                            {mapsStatus && (
                                <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>
                                    {mapsStatus === 'ok'
                                        ? <i className="fa fa-check-circle" style={{ color: '#16a34a' }} />
                                        : <i className="fa fa-times-circle" style={{ color: '#ef4444' }} />}
                                </span>
                            )}
                        </div>
                        {mapsStatus === 'ok' && <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}><i className="fa fa-check" style={{ marginRight: 4 }} />{t('map_coords_found')}</div>}
                        {mapsStatus === 'fail' && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}><i className="fa fa-times" style={{ marginRight: 4 }} />{t('map_coords_not_found')}</div>}
                    </Form.Group>

                    <Row className="mb-2">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={labelStyle}>{t('latitude')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                <Form.Control name="latitude" type="number" step="any" value={addressData.latitude} onChange={handleChange}
                                    style={{ ...inputStyle, fontFamily: 'monospace', borderColor: errors.latitude ? '#ef4444' : undefined }} />
                                {errors.latitude && <div style={errStyle}>{errors.latitude}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={labelStyle}>{t('longitude')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                <Form.Control name="longitude" type="number" step="any" value={addressData.longitude} onChange={handleChange}
                                    style={{ ...inputStyle, fontFamily: 'monospace', borderColor: errors.longitude ? '#ef4444' : undefined }} />
                                {errors.longitude && <div style={errStyle}>{errors.longitude}</div>}
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Map preview link */}
                    {hasCoords && (
                        <a
                            href={`https://www.google.com/maps?q=${addressData.latitude},${addressData.longitude}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#6366f1', textDecoration: 'none', marginTop: 4 }}
                        >
                            <i className="fas fa-external-link-alt" style={{ fontSize: 10 }} />ดูใน Google Maps
                        </a>
                    )}

                    {/* Default toggle */}
                    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
                        <Form.Check
                            type="checkbox" id="isDefault"
                            label={t('set_as_default')}
                            checked={addressData.isDefault}
                            onChange={(e) => setAddressData(prev => ({ ...prev, isDefault: e.target.checked }))}
                            style={{ fontSize: 13, color: '#374151' }}
                        />
                    </div>

                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', padding: '14px 24px', gap: 8 }}>
                    <Button variant="light" onClick={handleCloseModal} style={{ fontSize: 14, borderRadius: 8 }}>{t('cancel')}</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving} style={{ fontSize: 14, borderRadius: 8, fontWeight: 600, minWidth: 100 }}>
                        {saving ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />{t('saving')}</> : t('save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

const addBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 8, color: '#4f46e5', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const textBtnStyle = { fontSize: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6b7280', fontWeight: 500 };
const sectionLabelStyle = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, marginTop: 4 };
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle = { fontSize: 14, borderRadius: 8, border: '1.5px solid #e5e7eb' };
const errStyle = { fontSize: 11, color: '#ef4444', marginTop: 3 };

export default AddressesBox;
